import { useEffect, useState } from "react";
import { RefreshCw, Search, Video } from "lucide-react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/ui/useDebouncedValue";
import {
  describeMeetingFailure,
  formatMeetingDuration,
  formatMeetingMoment,
  isLiveMeeting,
  PROVIDER_LABELS,
  STATE_LABELS,
} from "@/lib/agent/genieMeetings";
import { AdminGenieMeeting, GenieMeetingState } from "@/types/agent/genie-meeting";
import { AdminMeetingDetailsSheet } from "./AdminMeetingDetailsSheet";
import { ADMIN_MEETINGS_PAGE_SIZE, useAdminGenieMeetings } from "./hooks/useAdminGenieMeetings";

const SEARCH_DEBOUNCE_MS = 350;
const COLUMN_COUNT = 7;

const getStateBadge = (meeting: AdminGenieMeeting) => {
  const label = STATE_LABELS[meeting.state];
  if (meeting.state === "completed") {
    return <Badge className="bg-genie-primary/10 text-genie-primary border-genie-primary/20">{label}</Badge>;
  }
  if (meeting.state === "failed") return <Badge variant="destructive">{label}</Badge>;
  if (isLiveMeeting(meeting)) {
    return (
      <Badge variant="outline" className="gap-1.5 text-genie-secondary border-genie-secondary/40">
        <span className="h-1.5 w-1.5 rounded-full bg-genie-secondary animate-pulse" aria-hidden />
        {label}
      </Badge>
    );
  }
  if (meeting.state === "cancelled") {
    return (
      <Badge variant="outline" className="text-genie-text-muted">
        {label}
      </Badge>
    );
  }
  return <Badge variant="secondary">{label}</Badge>;
};

/**
 * Every genie meeting on the platform, for reviewing what happened and
 * debugging a stuck or failed one. Rows open a read-only sheet with the ids
 * (Nylas notetaker included) and the full Activity list.
 */
const GenieMeetingsPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState<GenieMeetingState | "all">("all");
  const [selected, setSelected] = useState<AdminGenieMeeting | null>(null);
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isFetching, refetch } = useAdminGenieMeetings(
    { state: stateFilter === "all" ? undefined : stateFilter, search: debouncedSearch || undefined },
    page,
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, stateFilter]);

  const meetings = data?.meetings ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Video className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Genie Meetings</h1>
              <p className="text-genie-text-secondary">
                Every meeting a genie was sent to: what happened, and why
              </p>
            </div>
          </div>
          <Button onClick={() => refetch()} variant="outline" className="gap-2 min-h-[44px]" disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search by meeting title or link, genie, or owner name or email..."
                className="pl-10 text-base sm:text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={stateFilter} onValueChange={(value) => setStateFilter(value as GenieMeetingState | "all")}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(Object.keys(STATE_LABELS) as GenieMeetingState[]).map((state) => (
                  <SelectItem key={state} value={state}>
                    {STATE_LABELS[state]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-genie-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Meeting</TableHead>
                <TableHead>Genie</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden lg:table-cell">Why</TableHead>
                <TableHead className="hidden xl:table-cell">Length</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-secondary">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Loading meetings...
                  </TableCell>
                </TableRow>
              ) : meetings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-muted">
                    No meetings found
                  </TableCell>
                </TableRow>
              ) : (
                meetings.map((meeting) => (
                  <TableRow
                    key={meeting.id}
                    onClick={() => setSelected(meeting)}
                    className="cursor-pointer hover:bg-genie-primary/5"
                  >
                    <TableCell className="text-sm text-genie-text-secondary whitespace-nowrap">
                      {formatMeetingMoment(meeting.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-genie-text-primary">
                        {meeting.title || PROVIDER_LABELS[meeting.meeting_provider]}
                      </div>
                      {meeting.title && (
                        <div className="text-xs text-genie-text-secondary">{PROVIDER_LABELS[meeting.meeting_provider]}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-genie-text-primary">{meeting.agent_name ?? "—"}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm text-genie-text-primary">{meeting.owner_name ?? "—"}</div>
                      {meeting.owner_email && (
                        <CopyableEmail email={meeting.owner_email} className="text-xs text-genie-text-secondary" />
                      )}
                    </TableCell>
                    <TableCell>{getStateBadge(meeting)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-genie-text-secondary max-w-xs truncate">
                      {meeting.state === "failed" ? describeMeetingFailure(meeting.failure_reason) : "—"}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-sm text-genie-text-secondary">
                      {formatMeetingDuration(meeting.duration_secs) ?? "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalCount > ADMIN_MEETINGS_PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * ADMIN_MEETINGS_PAGE_SIZE + 1} to{" "}
                {Math.min(page * ADMIN_MEETINGS_PAGE_SIZE, totalCount)} of {totalCount} meetings
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none min-h-[44px]"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * ADMIN_MEETINGS_PAGE_SIZE >= totalCount}
                  className="flex-1 sm:flex-none min-h-[44px]"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AdminMeetingDetailsSheet meeting={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default GenieMeetingsPage;
